import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import CustomStore from "devextreme/data/custom_store";
import DataSource from "devextreme/data/data_source";
import { createStore } from "devextreme-aspnet-data-nojquery";
import apiRoutes from "../../assets/apiRoutes.json";
import { ModuleNames } from "../../enum/Enum";
import { Security } from "../../const/constants";
import SelectBox from "devextreme-react/select-box";
import { Button } from "devextreme-react/button";
import Popup from "devextreme-react/popup";
import List from "devextreme-react/list";
import notify from "devextreme/ui/notify";
import { confirm } from "devextreme/ui/dialog";
import DataGrid, {
  Column,
  Editing,
  Lookup,
  Paging,
  Selection,
  Sorting,
  Button as GridButton,
  Toolbar,
  Item,
} from "devextreme-react/data-grid";
import {
  getGroups,
  insertGroup,
  updateGroup,
  deleteGroup,
  getGroupItems,
  insertItem,
  updateItem,
  deleteItem,
  resetGroup,
  resetItem,
} from "../../services/ReportGroupService";

// Stable references for the "Add report" list (inline literals would change identity
// every render and make the DevExtreme List re-register its template in a loop).
const AVAILABLE_SEARCH_EXPR = ["ReportKey", "Description"];

// One page of the Add-report list. The list scrolls to fetch more, so this is all that is
// pulled when the popup opens.
const AVAILABLE_PAGE_SIZE = 50;
const renderAvailableReport = (r) =>
  r ? `${r.Description || r.ReportKey} — ${r.SourceTableName === "ABLAYOUT" ? "ART" : r.ReportKey}` : "";

// A pristine default cannot be deleted or reset; only a changed (override) or
// user-created row (IsSystemDefault === false) exposes those actions.
const isChangedRow = (e) => !(e.row && e.row.data && e.row.data.IsSystemDefault);

function ReportGroups(props) {
  const accessToken = props.oidc?.user?.access_token;
  // null while the module list loads, so the dropdown never flashes modules the user has no
  // licence for; `module` is picked from that list once it arrives.
  const [modules, setModules] = useState(null);
  const [module, setModule] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [addVisible, setAddVisible] = useState(false);
  // Bumped on every Add-report open so the list's DataSource is rebuilt rather than reused.
  const [addSeq, setAddSeq] = useState(0);
  const [selectedAvailable, setSelectedAvailable] = useState([]);

  const groupsCache = useRef({});
  const itemsCache = useRef({});
  const groupsGridRef = useRef(null);
  const itemsGridRef = useRef(null);

  // Offer exactly the modules the role-permissions screen lists. Permission.js builds its module
  // menu from the GetRolePermissions catalog (loaded by Roles.js), which the auth server already
  // filters by the customer's licence - so reading the same endpoint and deriving the list the same
  // way keeps the two screens in step without a second source of truth.
  //
  // There is deliberately NO fallback to a local module list. An unreachable endpoint used to fall
  // back to every module, which is indistinguishable from "everything is licensed" - the dropdown
  // looked hardcoded and the failure stayed hidden. The backend is now the only source: if it
  // cannot answer, the dropdown is empty and says so.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${window._env_.REACT_APP_AUTH_URL}/${apiRoutes.idp.GetRolePermissions}`, {
        headers: { "Content-Type": "application/json", access_token: accessToken },
      })
      .then((res) => {
        if (cancelled) return;
        const catalog = Array.isArray(res.data) ? res.data : [];
        // One entry per module. The catalog holds a row per permission GROUP, so a module repeats
        // once per group it owns - hence the dedupe, exactly as Permission.js does it.
        const byCode = new Map();
        catalog.forEach((grp) => {
          const code = grp && grp.module;
          if (!code || byCode.has(code)) return;
          // moduleFullName is the label the role-permissions screen shows; ModuleNames covers the
          // case where a permission carries no LinkedToModule and the API leaves it null.
          byCode.set(code, { id: code, name: grp.moduleFullName || ModuleNames[code] || code });
        });
        // Permission.js's ordering: Security last, everything else by module CODE. Sorting by the
        // displayed name instead would list the modules in a different order than that screen.
        const allowed = [...byCode.values()].sort((a, b) => {
          if (a.id === Security) return 1;
          if (b.id === Security) return -1;
          return a.id.localeCompare(b.id);
        });
        if (allowed.length === 0) {
          console.warn("GetRolePermissions returned no module. Raw response:", res.data);
        }
        setModules(allowed);
        setModule((current) => current || allowed[0]?.id || null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("GetRolePermissions failed", err);
        setModules([]);
        notify({ message: "Could not load the modules", width: 300 }, "error", 3000);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  // Clear the selection when the module changes (clears the items grid).
  useEffect(() => {
    setSelectedGroup(null);
  }, [module]);

  // Keyed by GroupKey: the group's stable identity (the default row's ROWID). GroupName
  // is editable, and the effective row's RowId flips between default and override, but
  // GroupKey is stable. Company is stamped server-side from the current user.
  const groupStore = useMemo(
    () =>
      new CustomStore({
        key: "GroupKey",
        loadMode: "raw",
        load: async () => {
          // No module yet (licensed list still loading, or none licensed) - an empty module would
          // ask the API for every module's groups.
          if (!module) return [];
          const data = (await getGroups(module, accessToken)) || [];
          groupsCache.current = {};
          data.forEach((g) => (groupsCache.current[g.GroupKey] = g));
          return data;
        },
        insert: (values) => insertGroup({ ...values, Module: module }, accessToken),
        update: (key, values) => updateGroup({ ...groupsCache.current[key], ...values }, accessToken),
        remove: (key) => deleteGroup(groupsCache.current[key]?.RowId, accessToken),
      }),
    [module, accessToken]
  );

  // Keyed by Name: the report's unique, read-only identity. The effective row's RowId
  // flips between the default and its override; edits/deletes resolve the RowId via cache.
  const itemStore = useMemo(
    () =>
      new CustomStore({
        key: "Name",
        loadMode: "raw",
        load: async () => {
          if (!selectedGroup) return [];
          const data = (await getGroupItems(selectedGroup.RowId, accessToken)) || [];
          itemsCache.current = {};
          data.forEach((i) => (itemsCache.current[i.Name] = i));
          return data;
        },
        update: (key, values) => updateItem({ ...itemsCache.current[key], ...values }, accessToken),
        remove: (key) => deleteItem(itemsCache.current[key]?.RowId, accessToken),
      }),
    [selectedGroup, accessToken]
  );

  const onGroupSelectionChanged = useCallback((e) => {
    setSelectedGroup((e.selectedRowsData && e.selectedRowsData[0]) || null);
  }, []);

  const onResetGroup = useCallback(
    async (e) => {
      const groupKey = e.row?.data?.GroupKey;
      if (!groupKey) return;
      if (!(await confirm("Restore this group to its default name?", "Restore group"))) return;
      try {
        await resetGroup(groupKey, accessToken);
        notify({ message: "Group restored to default", width: 220 }, "success", 2000);
        groupsGridRef.current.instance().refresh();
        itemsGridRef.current.instance().refresh();
      } catch (err) {
        notify({ message: "Could not restore the group", width: 240 }, "error", 3000);
      }
    },
    [accessToken]
  );

  const onResetItem = useCallback(
    async (e) => {
      const name = e.row?.data?.Name;
      if (!name) return;
      if (!(await confirm("Restore this report to its default values?", "Restore report"))) return;
      try {
        // Scoped to the selected group: the same report may be placed in other groups of this
        // module and each placement keeps its own override.
        await resetItem(module, name, selectedGroup?.GroupKey, accessToken);
        notify({ message: "Report restored to default", width: 220 }, "success", 2000);
        itemsGridRef.current.instance().refresh();
      } catch (err) {
        notify({ message: "Could not restore the report", width: 240 }, "error", 3000);
      }
    },
    [module, selectedGroup, accessToken]
  );

  // Server-paged feed for the Add-report list: DataSourceLoader on the API turns paginate/
  // pageSize/sort/search into SQL, so opening the popup costs one page rather than every report
  // definition in the database. Rebuilt per module so a module switch starts from a clean page 1,
  // and per open (addSeq) so each visit starts fresh - see openAddReports.
  const availableStore = useMemo(
    () =>
      new DataSource({
        store: createStore({
          key: "ReportKey",
          loadUrl: `${window._env_.REACT_APP_APIS}/${apiRoutes.reportGroups.GetAvailableReports}`,
          loadParams: { module },
          onBeforeSend: (method, ajaxOptions) => {
            ajaxOptions.headers = {
              access_token: accessToken,
              Authorization: `Bearer ${accessToken}`,
            };
          },
        }),
        paginate: true,
        pageSize: AVAILABLE_PAGE_SIZE,
        // A definite sort is required: OFFSET/FETCH over the ABRUNPROGRAM + ABLAYOUT union has no
        // inherent order, so without it page boundaries could repeat or skip rows.
        sort: [{ selector: "ReportKey", desc: false }],
      }),
    [module, accessToken, addSeq]
  );

  const openAddReports = useCallback(() => {
    if (!selectedGroup) {
      notify({ message: "Select a group first", width: 250 }, "warning", 2500);
      return;
    }
    setSelectedAvailable([]);
    // Discards the previous visit's DataSource. Closing the popup unmounts the List, so its search
    // box resets, but a DataSource keeps its searchValue, page index and cached rows - reusing one
    // re-served the last filter's results under an empty search box.
    setAddSeq((n) => n + 1);
    setAddVisible(true);
  }, [selectedGroup]);

  const addSelectedReports = useCallback(async () => {
    if (!selectedGroup || selectedAvailable.length === 0) {
      setAddVisible(false);
      return;
    }
    try {
      let sort = 0;
      for (const r of selectedAvailable) {
        await insertItem(
          {
            RGroup: selectedGroup.GroupKey, // stable group id (the default ROWID), never deleted
            SourceTableName: r.SourceTableName,
            RSource: r.RSource || r.RowId,
            Name: r.Name || r.ReportKey, // unique identifier used to load the report
            Module: module,
            Description: null, // inherit the report's own label unless edited later
            SortOrder: ++sort,
          },
          accessToken
        );
      }
      notify({ message: "Report(s) added", width: 200 }, "success", 2000);
      setAddVisible(false);
      itemsGridRef.current.instance().refresh();
    } catch (err) {
      notify({ message: "Could not add report(s)", width: 250 }, "error", 3000);
    }
  }, [selectedGroup, selectedAvailable, module, accessToken]);
    // Select the first group once, on the first load that actually returns rows. The module is not
    // known until GetModules answers, so the very first load is empty - claiming "loaded" then
    // would leave the screen with no group selected.
    const onContentReady = (e) => {
        if (!e.component._hasLoaded) {
            var rows = e.component.getVisibleRows();
            if (!rows || rows.length === 0) return;
            e.component._hasLoaded = true;
            var selection = e.component.getSelectedRowKeys();
            if (selection.length == 0) {
                e.component.selectRowsByIndexes([0]);
            }
        }
    };
        const onEditingStart = (e) => {
        if(e.key != null && e.key != undefined && !groupsGridRef.current.instance().isRowSelected(e.key))
        {
            groupsGridRef.current.instance().selectRows([e.key], false);
        }
    };
  return (
    <div style={{ padding: 16 }}>
      <h3>Report groups</h3>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
        <div style={{ width: 280 }}>
          <SelectBox
            items={modules || []}
            valueExpr="id"
            displayExpr="name"
            value={module}
            label="Module"
            stylingMode="outlined"
            labelMode="static"
            searchEnabled={true}
            searchExpr={["name", "id"]}
            noDataText={modules ? "No licensed modules" : "Loading..."}
            onValueChanged={(e) => setModule(e.value)}
          />
        </div>
      </div>

      <h4>Groups</h4>
      <DataGrid
        ref={groupsGridRef}
        dataSource={groupStore}
        showBorders={true}
        onSelectionChanged={onGroupSelectionChanged}
        height={280}
        onContentReady={onContentReady}
       onEditingStart={onEditingStart}
      >
        <Sorting mode="multiple" />
        <Paging defaultPageSize={10} />
        <Selection mode="single" />
        <Editing mode="cell" allowAdding={true} allowUpdating={true} allowDeleting={true} />
        <Column dataField="GroupName" caption="Group name" />
        <Column dataField="Module" caption="Module" allowEditing={false} width={80} />
        <Column dataField="SortOrder" caption="Sort" dataType="number" width={90} />
        <Column type="buttons" width={120}>
          <GridButton name="edit" />
          <GridButton name="delete" visible={isChangedRow} />
          <GridButton hint="Restore default" icon="refresh" onClick={onResetGroup} visible={isChangedRow} />
        </Column>
      </DataGrid>

      <div style={{ display: "flex", alignItems: "center", marginTop: 20, gap: 12 }}>
        <h4 style={{ margin: 0 }}>
          Reports {selectedGroup ? `in "${selectedGroup.GroupName}"` : ""}
        </h4>
      </div>

      <DataGrid
        ref={itemsGridRef}
        dataSource={itemStore}
        showBorders={true}
        height={320}
        noDataText={selectedGroup ? "No reports in this group" : "Select a group above"}
        paging={false}
      >
        <Sorting mode="multiple" />
        <Editing mode="cell" allowUpdating={true} allowDeleting={true} />
        <Column dataField="Description" caption="Menu label" />
        <Column dataField="Name" caption="Report name" allowEditing={false} width={130} />
        <Column dataField="SourceTableName" caption="Source" allowEditing={false} width={120} />
        <Column dataField="SortOrder" caption="Sort" dataType="number" width={80} />
        <Column type="buttons" width={120}>
          <GridButton name="edit" />
          <GridButton name="delete" visible={isChangedRow} />
          <GridButton hint="Restore default" icon="refresh" onClick={onResetItem} visible={isChangedRow} />
        </Column>
        <Toolbar>
          <Item location="after">
            <Button text="Add report" type="default" disabled={!selectedGroup} onClick={openAddReports} />
          </Item>
        </Toolbar>
      </DataGrid>

      {addVisible && (
        <Popup
          visible={true}
          onHiding={() => setAddVisible(false)}
          title="Add report to group"
          showCloseButton={true}
          width={620}
          height={540}
        >
          <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <List
              dataSource={availableStore}
              height="100%"
              showSelectionControls={true}
              selectionMode="multiple"
              searchEnabled={true}
              searchExpr={AVAILABLE_SEARCH_EXPR}
              searchMode="contains"
              displayExpr={renderAvailableReport}
              // Fetches the next page as the user reaches the bottom instead of up front. Search
              // goes to the server too, so a term finds matches beyond the pages already loaded.
              pageLoadMode="scrollBottom"
              onSelectedItemsChange={setSelectedAvailable}
            />
            <div style={{ marginTop: 12, textAlign: "right" }}>
              <Button text="Add selected" type="default" onClick={addSelectedReports} />
            </div>
          </div>
        </Popup>
      )}
    </div>
  );
}

function mapStateToProps(state) {
  return { oidc: state.oidc };
}

export default connect(mapStateToProps)(ReportGroups);
